// UpcomingMissionsAgenda: Main container for the agenda list view
// Renders all days in the selected period with their missions grouped by date
// Handles loading, error, empty filter states
// CREATED: Part of the agenda list view for TeamView
// NOTE: On desktop, this component is the sole scroll container (overflow-y-auto flex-1 min-h-0).
//       It must be a direct child of a flex-col container for flex-1 to constrain its height
//       and enable sticky day headers in AgendaDaySection to work correctly.

import { useMemo } from 'react';
import { AlertCircle, Layers, Users, CalendarDays } from 'lucide-react';
import { AgendaDaySection } from './AgendaDaySection';
import { AgendaLoadingSkeleton } from './AgendaLoadingSkeleton';
import { formatDate } from '../../../lib/dateUtils';
import type { EnrichedMission } from './types';
import type { Service } from '../../../lib/services';
import type { AssignedTaskWithDetails } from '../../../lib/execution';

interface UpcomingMissionsAgendaProps {
  tasks: EnrichedMission[];
  periodStart: Date;
  periodEnd: Date;
  services: Service[];
  loading: boolean;
  error: string | null;
  onMissionClick: (task: AssignedTaskWithDetails) => void;
  periodType: 'week' | 'month';
  isDesktop: boolean;
  onRetry: () => void;
  hasServiceFilter: boolean;
  hasProfileFilter: boolean;
}

function generateDatesInRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const current = new Date(start);
  current.setHours(0, 0, 0, 0);
  const endNorm = new Date(end);
  endNorm.setHours(0, 0, 0, 0);
  while (current <= endNorm) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

export function UpcomingMissionsAgenda({
  tasks,
  periodStart,
  periodEnd,
  services,
  loading,
  error,
  onMissionClick,
  periodType,
  isDesktop,
  onRetry,
  hasServiceFilter,
  hasProfileFilter,
}: UpcomingMissionsAgendaProps) {
  const tasksByDate = useMemo(() => {
    const map = new Map<string, EnrichedMission[]>();
    tasks.forEach(task => {
      const key = task.assigned_date;
      const existing = map.get(key) || [];
      existing.push(task);
      map.set(key, existing);
    });
    map.forEach((dayTasks, key) => {
      map.set(key, dayTasks.sort((a, b) => {
        if (!a.scheduled_time && !b.scheduled_time) return 0;
        if (!a.scheduled_time) return 1;
        if (!b.scheduled_time) return -1;
        return a.scheduled_time.localeCompare(b.scheduled_time);
      }));
    });
    return map;
  }, [tasks]);

  const dates = useMemo(() => generateDatesInRange(periodStart, periodEnd), [periodStart, periodEnd]);

  const containerClass = isDesktop
    ? 'bg-white rounded-xl border border-gray-200 shadow-sm overflow-y-auto flex-1 min-h-0'
    : 'bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden';

  if (!hasServiceFilter) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-12">
        <div className="text-center">
          <Layers className="mx-auto text-primary-400 mb-4" size={40} />
          <p className="text-gray-600 text-sm">
            Sélectionnez un ou plusieurs services pour afficher les missions à venir
          </p>
        </div>
      </div>
    );
  }

  if (!hasProfileFilter) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-12">
        <div className="text-center">
          <Users className="mx-auto text-primary-400 mb-4" size={40} />
          <p className="text-gray-600 text-sm">
            Veuillez sélectionner au moins un membre de l'équipe pour afficher les missions
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <AgendaLoadingSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-12">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={40} />
          <p className="text-gray-600 mb-4 text-sm">{error}</p>
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-12">
        <div className="text-center">
          <CalendarDays className="mx-auto text-gray-300 mb-4" size={40} />
          <p className="text-gray-500 text-sm">Aucune mission planifiée sur cette période</p>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {dates.map(date => {
        const key = formatDate(date);
        const missions = tasksByDate.get(key) || [];
        return (
          <AgendaDaySection
            key={key}
            date={date}
            missions={missions}
            services={services}
            onMissionClick={onMissionClick}
            periodType={periodType}
            isDesktop={isDesktop}
          />
        );
      })}
    </div>
  );
}
